import { toast } from "@/components/common/Toast"
import {
  EditResponsePairKey,
  isChangeActionPayload,
  isResetActionPayload,
  ResetActionPayload,
  TextAreaCache,
  TextAreaCount,
  TextAreaState,
  TextAreaStateAction,
  TextAreaStateActionType,
} from "@/types/hooks/useTextAreaStates"
import {
  EditResponsePairArgs,
  Questionnaire,
  QuestionnaireSection,
} from "@/types/questionnaire/models"
import { isLimitError } from "@/utils/limitErrors"
import { UseMutateFunction } from "@tanstack/react-query"
import { useCallback, useEffect, useReducer } from "react"
import {
  TranslationKeys,
  useTranslation,
} from "../useTranslation/useTranslation"

function getInitialTexts(
  data: Questionnaire | undefined,
  editResponsePairKey: EditResponsePairKey,
): TextAreaCache {
  const initialTexts: TextAreaCache = {}

  if (!data || !data.sections) {
    return initialTexts
  }

  for (let i = 0; i < data.sections.length; i++) {
    const section = data.sections[i]
    initialTexts[section.id] = {}

    for (let j = 0; j < section.responsePairs.length; j++) {
      const responsePair = section.responsePairs[j]
      initialTexts[section.id][responsePair.id] =
        responsePair[editResponsePairKey] || ""
    }
  }

  return initialTexts
}

function reduceTextsSectionCount(texts: Record<string, string | null>): number {
  return Object.values(texts).reduce((count, text) => {
    return text ? count + 1 : count
  }, 0)
}

function reduceTextsTotalCount(texts: TextAreaCache): number {
  return Object.values(texts).reduce((total, sectionTexts) => {
    return total + reduceTextsSectionCount(sectionTexts)
  }, 0)
}

function textsReducer(
  state: TextAreaState,
  action: TextAreaStateAction,
): TextAreaState {
  if (isResetActionPayload(action.payload)) {
    return action.payload.state
  }

  switch (action.type) {
    case TextAreaStateActionType.Change: {
      const { sectionId, responsePairId, text } = action.payload
      if (!sectionId || !responsePairId) {
        return state
      }

      const updatedTexts = {
        ...state.texts,
        [sectionId]: {
          ...state.texts[sectionId],
          [responsePairId]: text,
        },
      }

      const updatedSectionCount = reduceTextsSectionCount(
        updatedTexts[sectionId],
      )
      const updatedTotalCount = reduceTextsTotalCount(updatedTexts)

      return {
        texts: updatedTexts,
        textsCount: {
          ...state.textsCount,
          sections: {
            ...state.textsCount.sections,
            [sectionId]: updatedSectionCount,
          },
          total: updatedTotalCount,
        },
      }
    }
    default: {
      throw Error("Unknown action: " + action.type)
    }
  }
}

function initTextsCount(
  data: Questionnaire | undefined,
  textsCache: TextAreaCache,
  editResponsePairKey: EditResponsePairKey,
): TextAreaCount {
  const sectionsState = { total: 0, sections: {} } as TextAreaCount
  if (!data) {
    return sectionsState
  }

  data.sections.forEach((section) => {
    const sectionTextsCount = calcInitialTextsSectionCount(
      section,
      editResponsePairKey,
    )
    sectionsState.sections[section.id] = sectionTextsCount
  })

  sectionsState.total = reduceTextsTotalCount(textsCache)

  return sectionsState
}

function calcInitialTextsSectionCount(
  section: QuestionnaireSection,
  editResponsePairKey: EditResponsePairKey,
): number {
  return section.responsePairs.reduce((responseCount, responsePair) => {
    return responsePair[editResponsePairKey] !== null &&
      responsePair[editResponsePairKey] !== ""
      ? responseCount + 1
      : responseCount
  }, 0)
}

function dispatchWithApi(
  action: TextAreaStateAction,
  mutateResponsePair: UseMutateFunction<
    Questionnaire,
    Error,
    EditResponsePairArgs
  >,
  dispatch: React.Dispatch<TextAreaStateAction>,
  questionnaireId: string | undefined,
  editResponsePairKey: EditResponsePairKey,
  translate: (identifier: TranslationKeys) => string,
  showToastMessage: boolean = true,
) {
  if (!isChangeActionPayload(action.payload)) {
    dispatch(action)
    return
  }

  switch (action.type) {
    case TextAreaStateActionType.Change: {
      const { responsePairId, text } = action.payload

      if (!questionnaireId) {
        toast.error(translate("QUESTIONNAIRE_ANSWERS/TOAST_EDIT_ANSWER_ERROR"))
        break
      }

      const mutateArgs: EditResponsePairArgs = {
        questionnaireId,
        responsePairId,
      }
      mutateArgs[editResponsePairKey] = text

      // TODO: Are toasts the best option for notifying the user here?
      mutateResponsePair(mutateArgs, {
        onSuccess: () => {
          dispatch(action)
          if (showToastMessage) {
            if (editResponsePairKey === "response") {
              toast.success(
                translate("QUESTIONNAIRE_ANSWERS/TOAST_EDIT_ANSWER_SUCCESS"),
              )
            } else if (editResponsePairKey === "question") {
              toast.success(
                translate(
                  "QUESTIONNAIRE_QUESTIONS/TOAST_EDIT_QUESTION_SUCCESS",
                ),
              )
            }
          }
        },
        onError: (error) => {
          // A limit or trial error already got the interceptor's upgrade toast
          if (showToastMessage && !isLimitError(error)) {
            if (editResponsePairKey === "response") {
              toast.error(
                translate("QUESTIONNAIRE_ANSWERS/TOAST_EDIT_ANSWER_ERROR"),
              )
            } else if (editResponsePairKey === "question") {
              toast.error(
                translate("QUESTIONNAIRE_QUESTIONS/TOAST_EDIT_QUESTION_ERROR"),
              )
            }
          }
        },
      })
      break
    }
    default: {
      dispatch(action)
      break
    }
  }
}

export function useTextAreaStates(
  data: Questionnaire | undefined,
  mutateResponsePair: UseMutateFunction<
    Questionnaire,
    Error,
    EditResponsePairArgs
  >,
  editResponsePairKey: EditResponsePairKey,
  showToastMessage: boolean = true,
) {
  const { translate } = useTranslation()
  const initialTexts = getInitialTexts(data, editResponsePairKey)
  const initialTextsCount = initTextsCount(
    data,
    initialTexts,
    editResponsePairKey,
  )
  const initialState = {
    texts: initialTexts,
    textsCount: initialTextsCount,
  } satisfies TextAreaState

  const [state, dispatch] = useReducer(textsReducer, initialState)

  const dispatchWrapper = useCallback(
    (action: TextAreaStateAction) => {
      dispatchWithApi(
        action,
        mutateResponsePair,
        dispatch,
        data?.id,
        editResponsePairKey,
        translate,
        showToastMessage,
      )
    },
    [
      mutateResponsePair,
      data?.id,
      editResponsePairKey,
      translate,
      showToastMessage,
    ],
  )

  useEffect(() => {
    // Recompute initial state from current data to ensure fresh values on data change
    const freshTexts = getInitialTexts(data, editResponsePairKey)
    const freshTextsCount = initTextsCount(
      data,
      freshTexts,
      editResponsePairKey,
    )
    const freshState = {
      texts: freshTexts,
      textsCount: freshTextsCount,
    } satisfies TextAreaState

    dispatch({
      type: TextAreaStateActionType.Reset,
      payload: { state: freshState } satisfies ResetActionPayload,
    })
  }, [data, editResponsePairKey])

  return [state, dispatchWrapper] as const
}
